(ns hai.tasks.data
  "Shared task state, events, subscriptions, and domain helpers."
  (:require
   [clojure.string :as str]
   [hai.entity.staging :as staging]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.eql :as eql]
   [hai.ui.rf-sugar :refer [defevent defevent-fx defsub]]))

(defn- start-of-today []
  (doto (js/Date.) (.setHours 0 0 0 0)))

(defn task-bucket
  "Bucket a task by its `:task/urgency`. Explicit :now / :next map to those
   buckets; everything else — including untriaged tasks with no urgency set —
   lands in :later."
  [task]
  (case (:task/urgency task)
    :task.urgency/now  :now
    :task.urgency/next :next
    :later))

(defn staged-bucket
  "The bucket a task the composer just saved lands in. Its payload carries a
   bare `:urgency`, not the `:task/urgency` [[task-bucket]] reads."
  [{:keys [urgency]}]
  (task-bucket {:task/urgency urgency}))

(defn assignee-email
  "First email of a task's assignee; the attr may be a collection."
  [assignee]
  (let [email (:user/email assignee)]
    (if (coll? email) (first email) email)))

(defn assignee-name [assignee]
  (or (not-empty (:user/full-name assignee))
      (not-empty (assignee-email assignee))))

(defn contact-name
  "Display name for a contact picked in the assignee picker. That's the shape a
   staged, not-yet-saved assignee has — `task/assign` is what turns it into the
   user `assignee-name` reads."
  [contact]
  (or (not-empty (:contact/full-name contact))
      (not-empty (first (:contact/email contact)))))

(defn picked-assignee?
  "True when `assignee` is a fresh pick the picker staged and `task/assign` can
   apply — either a contact (`:contact/uuid`) or a raw email typed for someone
   not in the requester's contacts (`:user/email` with no `:user/uuid`). An
   untouched picker still holds the task's resolved assignee, which carries a
   `:user/uuid`; that's left alone."
  [assignee]
  (boolean (or (:contact/uuid assignee)
               (and (:user/email assignee) (not (:user/uuid assignee))))))

(defn assignee->assign-params
  "`task/assign` params for a staged pick — a contact by uuid, or a raw email.
   A typed email carries `:contact/add?` (the picker's \"Add contact\" checkbox):
   whether the backend should also save the address to the requester's personal
   contacts. Defaults to true when the pick predates the checkbox."
  [assignee]
  (if-let [contact-uuid (:contact/uuid assignee)]
    {:contact/uuid contact-uuid}
    {:user/email (assignee-email assignee)
     :contact/add? (get assignee :contact/add? true)}))

(defn assignee-me? [current-user assignee]
  (= (:user/uuid current-user)
     (:user/uuid assignee)))

(defn assignee-initials [assignee]
  (let [from-name (->> (str/split (or (:user/full-name assignee) "") #"\s+")
                       (keep first)
                       (take 2)
                       (apply str))
        email (assignee-email assignee)]
    (cond
      (seq from-name) (str/upper-case from-name)
      (seq email)     (str/upper-case (subs email 0 1)))))

;; Home's "My Tasks" mini-app loads the tasks assigned to the current user:
;; every open one, plus any completed earlier today so the day's finished items
;; still show (struck through). `:user/current-tasks` returns both in a single
;; request, newest first.
(defevent-fx ::load-tasks-assigned-to-me [_ & [extra-success-fx extra-error-fx]]
  {:resolve {:query [{(list :user/current-tasks {:completed-since (start-of-today)})
                      eql/task-eql}]
             :store-in [::load-result]
             :transform-fn (fn [resp] (get resp :user/current-tasks []))
             :on-success-fx (into [[:dispatch [::loaded]]] extra-success-fx)
             :on-error-fx (vec extra-error-fx)}})

;; The full /app/tasks page loads every task the user can see — their own and
;; ones shared with them — but only the open ones; completed tasks live in the
;; page's separate Completed archive. Newest first.
(defevent-fx ::load-all-my-tasks [_ _]
  {:resolve {:query [{(list :user.tasks/table
                            {:pagination {:per-page 500
                                          :attrs [:task/created-at]
                                          :direction :desc}
                             :filters {:task/open? true}})
                      [{:collection/items eql/task-eql}]}]
             :store-in [::load-result]
             :transform-fn (fn [resp] (get-in resp [:user.tasks/table :collection/items] []))
             :on-success-fx [[:dispatch [::loaded]]]}})

;; Both loads land in the same `::tasks` slice — the one both surfaces read.
(defevent ::loaded [db _]
  (-> db
      (assoc ::tasks (get db ::load-result []))
      (dissoc ::load-result)))

(defn- error-text
  "User-facing toast text for a failed save, from the `:on-error-fx` body."
  [response]
  (if (= :activity/url (-> response :com.wsscode.pathom3.error/error-data :attr))
    "Could not save task. Check that your links are valid URLs."
    "Could not save task. Try again."))

(defn- picked->assignee
  "Optimistic `:task/assigned-to` shape from a staged pick — a contact or a raw
   email. The same `:user/full-name` + `:user/email` shape the row reads, so the
   just-created task shows the assignee right away. It has no `:user/uuid` until
   `task/assign` resolves and swaps in the real user."
  [picked]
  (if (:contact/uuid picked)
    {:user/full-name (:contact/full-name picked)
     :user/email (:contact/email picked)}
    {:user/email (:user/email picked)}))

(def board-collection-id
  "Collection id of the tasks-page :query kanban board ([HAI-001-04]). Owned here,
  in the data layer, so data-layer success handlers (e.g. `::create-resolved`)
  can reconcile the board without a circular dependency on the page namespace;
  `hai.tasks.page` reads it back for the board's `:id` and its per-task
  re-resolves."
  ::board)

(defn- staged-chips
  "The composer stages whole entities; a cached task only carries the chip shape
   `task-eql` asks for. Empty families are dropped, so this merges onto a
   task without blanking what it already has. A tag typed fresh has no uuid
   until the server makes one — its name carries the chip until the re-resolve."
  [{:keys [contacts notes tasks tags]}]
  (into {}
        (remove (comp empty? val))
        {:task/contacts (mapv staging/contact-chip contacts)
         :task/notes (mapv staging/note-chip notes)
         :task/tasks (mapv staging/task-chip tasks)
         :task/tags (mapv staging/tag-chip tags)}))

(defevent-fx ::create
  [{:keys [db]} {:keys [title description urgency deadline urls assignee
                        contacts notes tasks tags] :as staged}
   & [extra-success-fx]]
  (let [new-uuid (random-uuid)
        now (js/Date.)
        deadline-params (deadline/value->params deadline)
        link-urls (vec (distinct urls))
        ;; A fresh pick (a contact, or a raw email) reassigns away from the
        ;; creator; an untouched picker still holding the creator leaves the
        ;; default assign-to-me alone.
        assignee-picked (when (picked-assignee? assignee) assignee)
        contact-uuids (mapv :contact/uuid contacts)
        note-uuids (mapv :note/uuid notes)
        task-uuids (mapv :task/uuid tasks)
        tag-uuids (vec (keep :group/uuid tags))
        tag-names (vec (keep :tag/name tags))
        task (merge (cond-> {:task/uuid new-uuid
                             :task/title title
                             :task/created-at now
                             :task/updated-at now
                             :task/assigned-to (if assignee-picked
                                                 (picked->assignee assignee-picked)
                                                 (select-keys (:auth/identity db)
                                                              [:user/uuid :user/full-name :user/email]))
                             :user.entity/permissions [:permission.type/admin]}
                      (seq description) (assoc :task/description description)
                      urgency (assoc :task/urgency urgency)
                      deadline-params (merge deadline-params)
                      (seq link-urls) (assoc :task/activities
                                             (mapv (fn [url] {:activity/url url}) link-urls)))
                    ;; Embed the staged links and tags so the just-created card
                    ;; shows their chips instantly, before any re-resolve
                    ;; ([HAI-001-10]).
                    (staged-chips staged))
        params (cond-> {:task/title title
                        :task/uuid new-uuid}
                 (seq description) (assoc :task/description description)
                 urgency (assoc :task/urgency urgency)
                 (seq link-urls) (assoc :activity/urls link-urls)
                 deadline-params (merge deadline-params)
                 ;; Linked entities picked in the create composer; the backend
                 ;; `task/create` accepts these additively, same as `task/update`.
                 (seq contact-uuids) (assoc :task/contact-uuids contact-uuids)
                 (seq note-uuids) (assoc :task/note-uuids note-uuids)
                 (seq task-uuids) (assoc :task/task-uuids task-uuids)
                 (seq tag-uuids) (assoc :task/tag-uuids tag-uuids)
                 (seq tag-names) (assoc :task/tag-names tag-names))]
    {:db (-> db
             (update ::tasks #(into [task] %))
             (assoc ::highlighted-uuid new-uuid))
     ;; Optimistically prepend to the :query board too ([HAI-001-10]): it renders
     ;; from `:collections`, not the `::tasks` slice above, so without this the
     ;; new card wouldn't show until the create re-resolves. Instant feedback,
     ;; consistent with the other per-task board actions ([HAI-001-07]); the
     ;; success re-resolve reconciles, and `::create-failed` rolls it back.
     ;; Mount-guarded inside `:table/add-row` (creation also fires off-board).
     :fx [[:dispatch [:table/add-row board-collection-id task]]]
     ;; Briefly flag the just-added row so the card can flash it, then let it
     ;; settle. Keyed by uuid so only this task lights up, not every row on load.
     :dispatch-later {:ms 2000 :dispatch [::clear-highlight]}
     :resolve {:query [{(list 'task/create params) [:task/uuid]}]
               :on-success-fx (into [[:dispatch [::create-resolved new-uuid assignee-picked]]]
                                    extra-success-fx)
               :on-error-fx [[:dispatch [::create-failed new-uuid '?body]]]}}))

(defevent ::clear-highlight [db _]
  (dissoc db ::highlighted-uuid))

(defevent-fx ::create-failed [{:keys [db]} new-uuid response]
  {:db (update db ::tasks
               (fn [tasks]
                 (filterv #(not= new-uuid (:task/uuid %)) tasks)))
   ;; Roll the optimistic board row back too ([HAI-001-10]), mount-guarded like
   ;; the add.
   :fx [[:dispatch [:table/remove-row board-collection-id :task/uuid new-uuid]]
        [:dispatch [:toast/error {:text (error-text response)}]]]})

(defevent-fx ::create-resolved
  [_ new-uuid assignee-picked]
  ;; The task now exists, so a picked assignee can be applied — `task/assign`
  ;; reassigns it off the creator and grants the assignee access. Its own
  ;; resolver swaps the optimistic contact for the real user.
  (let [;; `::create` optimistically prepended the new card to the :query board
        ;; ([HAI-001-10]); this re-resolve is the reconciling authority that
        ;; replaces the optimistic row with server truth (the [HAI-001-07]
        ;; patch-then-reconcile pattern). Mount-guarded: creation also fires
        ;; from the mobile add-sheet / universal bar, where the board isn't
        ;; rendered.
        reconcile-board [:dispatch [:table/resolve-if-mounted board-collection-id]]]
    {:fx (cond-> [[:dispatch [:toast/success {:text "Task added"}]]]
           ;; No reassignment: reconcile now (the optimistic row already
           ;; matches server truth bar server-normalised fields).
           (not assignee-picked) (conj reconcile-board)
           ;; A picked assignee resolves *after* this via `task/assign`; carry
           ;; the reconcile through it instead, so the board holds the
           ;; optimistic (picked-assignee) row until the final user lands -
           ;; reconciling now would flash the pre-assign creator over it.
           assignee-picked (conj [:dispatch [::assign new-uuid assignee-picked
                                             [reconcile-board]]]))}))

(defn- task-ident
  "Entity-store key for a task, matching `hai.entity.helpers/->ident` so the
  detail page reads the same copy through the shared `:entity` subscription."
  [task-uuid]
  [:task/uuid (uuid (str task-uuid))])

(defn- patch-task
  "Update one task by uuid in the `::tasks` slice and, when present, the shared
  entity store, so optimistic edits show on the list and the detail page alike."
  [db task-uuid f]
  (let [patch-in (fn [tasks]
                   (mapv (fn [task]
                           (if (= task-uuid (:task/uuid task)) (f task) task))
                         tasks))]
    (cond-> (update db ::tasks patch-in)
      (get-in db (task-ident task-uuid)) (update-in (task-ident task-uuid) f))))

;; Completion is optimistic: the patch alone moves the task between Home's open
;; and struck states (and off the page's open buckets), so no reload is needed.
;; A caller with its own dependent state — e.g. the tasks page's Completed
;; archive — passes it as `extra-success-fx`.
(defevent-fx ::complete [{:keys [db]} task-uuid & [extra-success-fx]]
  {:db (patch-task db task-uuid #(assoc % :task/completed-at (js/Date.)))
   :resolve {:query [{(list 'task/complete {:task/uuid task-uuid})
                      [:task/uuid :task/completed-at]}]
             :on-success-fx (vec extra-success-fx)}})

(defevent-fx ::uncomplete [{:keys [db]} task-uuid & [extra-success-fx]]
  {:db (patch-task db task-uuid #(dissoc % :task/completed-at))
   :resolve {:query [{(list 'task/uncomplete {:task/uuid task-uuid}) [:task/uuid]}]
             :on-success-fx (vec extra-success-fx)}})

(defevent-fx ::delete [{:keys [db]} task-uuid & [extra-success-fx]]
  (let [deleted (some #(when (= task-uuid (:task/uuid %)) %) (get db ::tasks []))]
    {:db (update db ::tasks (fn [tasks] (filterv #(not= task-uuid (:task/uuid %)) tasks)))
     ;; No join: `task/delete` returns `{}`, so asking it for `:task/uuid` back
     ;; leaves Pathom no path to it and reports the whole delete as failed.
     :resolve {:query [(list 'task/delete {:task/uuid task-uuid})]
               :on-success-fx (conj (vec extra-success-fx)
                                    [:dispatch [:toast/success {:text "Task deleted"}]])
               :on-error-fx [[:dispatch [::delete-failed deleted]]]}}))

;; Both loads sort newest first, so the restored task goes back by created-at
;; rather than to the head of the list it was cut from.
(defevent-fx ::delete-failed [{:keys [db]} task]
  {:db (cond-> db
         task (update ::tasks
                      (fn [tasks]
                        (vec (sort-by :task/created-at
                                      #(compare %2 %1)
                                      (conj (vec tasks) task))))))
   :fx [[:dispatch [:table/resolve-if-mounted board-collection-id]]
        [:dispatch [:toast/error {:text "Could not delete task. Try again."}]]]})

(defevent-fx ::update
  [{:keys [db]} {:keys [task-uuid title description urgency deadline clear-deadline?
                        clear-assignee? urls
                        tag-uuids tag-names untag-uuids contact-uuids note-uuids task-uuids]}
   & [extra-success-fx]]
  (let [prev-task (some #(when (= task-uuid (:task/uuid %)) %)
                        (get db ::tasks []))
        deadline-params (deadline/value->params deadline)
        link-urls (vec (distinct urls))
        patch (cond-> {}
                clear-assignee? (assoc :task/assigned-to nil)
                (some? title) (assoc :task/title title)
                (some? description) (assoc :task/description description)
                (some? urgency) (assoc :task/urgency urgency)
                deadline-params (merge deadline-params)
                clear-deadline? (assoc :task/deadline-at nil
                                       :task/deadline-all-day? nil
                                       :task/deadline-timezone nil)
                (some? urls) (assoc :task/activities
                                    (mapv (fn [url] {:activity/url url}) link-urls)))
        rollback (into {} (map (fn [k] [k (get prev-task k)])) (keys patch))
        ;; Tags and entity links carry no optimistic patch — they are not on the
        ;; cached card — so they ride the params only, and the re-resolve is what
        ;; shows them.
        params (cond-> (merge {:task/uuid task-uuid}
                              (dissoc patch :task/activities :task/assigned-to))
                 clear-deadline? (assoc :clear-deadline? true)
                 clear-assignee? (assoc :clear-assignee? true)
                 (some? urls) (assoc :activity/urls link-urls)
                 (seq tag-uuids) (assoc :task/tag-uuids (vec tag-uuids))
                 (seq tag-names) (assoc :task/tag-names (vec tag-names))
                 (seq untag-uuids) (assoc :task/untag-uuids (vec untag-uuids))
                 (seq contact-uuids) (assoc :task/contact-uuids (vec contact-uuids))
                 (seq note-uuids) (assoc :task/note-uuids (vec note-uuids))
                 (seq task-uuids) (assoc :task/task-uuids (vec task-uuids)))]
    {:db (patch-task db task-uuid #(merge % patch))
     :resolve {:query [{(list 'task/update params) [:task/uuid]}]
               :on-success-fx (vec extra-success-fx)
               :on-error-fx [[:dispatch [::update-failed task-uuid rollback '?body]]]}}))

(defn- unlinked-uuids
  "The uuids the composer dropped: what the task has linked, minus what came
   back staged."
  [id-key linked staged]
  (let [staged-uuids (set (keep id-key staged))]
    (vec (remove staged-uuids (keep id-key linked)))))

(defn- added-uuids
  "The uuids the composer newly staged: what came back staged, minus what the
   task already has. Tags need this diff — `task/update` asserts the requester
   owns every tag uuid it is sent, so re-sending one that is merely riding along
   rejects the whole save on a task shared by someone else."
  [id-key linked staged]
  (let [linked-uuids (set (keep id-key linked))]
    (vec (remove linked-uuids (keep id-key staged)))))

;; Apply the composer's staged fields to an existing task. The scalars, the url
;; set and the tag reconciliation ride one `task/update`, which also attaches
;; linked entities; dropping a linked entity needs its own `task/remove-*`, and
;; a freshly picked assignee still goes through `task/assign`.
(defevent-fx ::save-edit
  [{:keys [db]} task {:keys [title description urgency deadline urls contacts
                             notes tasks tags assignee] :as staged}
   & [extra-success-fx]]
  (let [task-uuid (:task/uuid task)
        assignee-picked? (picked-assignee? assignee)
        ;; An untouched picker still holds the task's assignee, so an empty one
        ;; on a task that has someone is the chip's ✕, not "didn't touch it".
        assignee-cleared? (boolean (and (:task/assigned-to task) (nil? assignee)))
        dropped-contacts (unlinked-uuids :contact/uuid (:task/contacts task) contacts)
        dropped-notes (unlinked-uuids :note/uuid (:task/notes task) notes)
        dropped-tasks (unlinked-uuids :task/uuid (:task/tasks task) tasks)
        tag-uuids (added-uuids :group/uuid (:task/tags task) tags)
        tag-names (vec (keep :tag/name tags))
        untag-uuids (unlinked-uuids :group/uuid (:task/tags task) tags)
        ;; Links and tags carry no patch of their own through `::update` — they
        ;; are not scalars on the card — so the staged chips land here, or the
        ;; surfaces that don't re-resolve would show yesterday's links.
        chips (staged-chips staged)
        success-fx (conj (vec extra-success-fx)
                         [:dispatch [:toast/success {:text "Task saved"}]])]
    {:db (patch-task db task-uuid
                     (fn [cached]
                       (-> cached
                           (assoc :task/contacts [] :task/notes [] :task/tasks []
                                  :task/tags [])
                           (merge chips))))
     :fx (-> [[:dispatch [::update {:task-uuid task-uuid
                                    :title title
                                    :description description
                                    :urgency urgency
                                    :deadline deadline
                                    :clear-deadline? (and (:task/deadline-at task)
                                                          (nil? (:date deadline)))
                                    :clear-assignee? assignee-cleared?
                                    :urls urls
                                    :tag-uuids tag-uuids
                                    :tag-names tag-names
                                    :untag-uuids untag-uuids
                                    ;; Deltas, like the tags above: `task/update`
                                    ;; asserts the requester owns every contact
                                    ;; and can read every note/task it is sent.
                                    ;; A task shared with you can carry links to
                                    ;; things you cannot open — re-sending one
                                    ;; that is merely riding along rejects the
                                    ;; whole save.
                                    :contact-uuids (added-uuids :contact/uuid (:task/contacts task) contacts)
                                    :note-uuids (added-uuids :note/uuid (:task/notes task) notes)
                                    :task-uuids (added-uuids :task/uuid (:task/tasks task) tasks)}
                          ;; The caller's re-resolve rides exactly one mutation.
                          ;; A picked assignee lands last through `task/assign`,
                          ;; so it carries the re-resolve instead — on both, the
                          ;; earlier round-trip can land after the later one and
                          ;; flash the pre-assign state.
                          (when-not assignee-picked? success-fx)]]]
             (into (for [contact-uuid dropped-contacts]
                     [:dispatch [::remove-contact {:task/uuid task-uuid
                                                   :contact/uuid contact-uuid}]]))
             (into (for [note-uuid dropped-notes]
                     [:dispatch [::remove-note-link {:task/uuid task-uuid
                                                     :note/uuid note-uuid}]]))
             (into (for [linked-uuid dropped-tasks]
                     [:dispatch [::remove-task-link {:task/uuid task-uuid
                                                     :task/linked-uuid linked-uuid}]]))
             (cond-> assignee-picked?
               (conj [:dispatch [::assign task-uuid assignee success-fx]])))}))

(defsub ::contacts [db]
  (get db ::contacts))

;; The search text that produced whatever is currently in `::contacts`. The
;; picker compares it against the live input to tell "search still in flight"
;; (query ≠ input) from "search done, nothing matched" (query = input), so it
;; doesn't flash "No matching contacts" between a keystroke and its response.
(defsub ::contacts-query [db]
  (get db ::contacts-query))

;; Server-side contact search backing the assignee picker. `:contact/search`
;; filters in Datomic by name/email and returns nothing for a blank query, so
;; the picker searches as-you-type rather than fetching the whole list once.
;; Contacts without an email are dropped here — the `task/assign` mutation
;; resolves the assignee by email, so an email-less contact can't be assigned.
(defevent-fx ::search-contacts [_ text]
  {:resolve {:query [{(list :contact/search {:contact/search-text text})
                      [:contact/uuid :contact/full-name :contact/email]}]
             :store-in [::contacts-load-result]
             :transform-fn (fn [resp]
                             (->> resp
                                  :contact/search
                                  (filter #(seq (:contact/email %)))))
             :on-success-fx [[:dispatch [::loaded-contacts text]]]}})

;; Commit results + the query that produced them together, so a consumer never
;; sees the new list paired with a stale query (or vice versa).
(defevent ::loaded-contacts [db text]
  (-> db
      (assoc ::contacts (get db ::contacts-load-result []))
      (assoc ::contacts-query text)
      (dissoc ::contacts-load-result)))

(defevent ::clear-contacts [db _]
  (dissoc db ::contacts ::contacts-query ::contacts-load-result))

(defevent-fx ::assign [{:keys [db]} task-uuid assignee & [extra-success-fx]]
  ;; Optimistic: the picked contact/email shows as the assignee right away —
  ;; the same shape ::create embeds — on the slice, the entity store, and the
  ;; :query board. `task/assign`'s response swaps in the real user, and an
  ;; error restores the previous assignee.
  (let [optimistic (picked->assignee assignee)
        previous (:task/assigned-to (or (some #(when (= task-uuid (:task/uuid %)) %)
                                              (get db ::tasks []))
                                        (get-in db (task-ident task-uuid))))]
    {:db (patch-task db task-uuid #(assoc % :task/assigned-to optimistic))
     :fx [[:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid
                      {:task/assigned-to optimistic}]]]
     :resolve {:query [{(list 'task/assign (merge {:task/uuid task-uuid}
                                                  (assignee->assign-params assignee)))
                        [:task/uuid {:task/assigned-to [:user/uuid :user/full-name :user/email]}]}]
               :on-success-fx (into [[:dispatch [::assign-resolved task-uuid '?body]]]
                                    extra-success-fx)
               :on-error-fx [[:dispatch [::assign-failed task-uuid previous]]]}}))

(defevent-fx ::assign-failed [{:keys [db]} task-uuid previous]
  {:db (patch-task db task-uuid #(assoc % :task/assigned-to previous))
   :fx [[:dispatch [:table/patch-row board-collection-id :task/uuid task-uuid
                    {:task/assigned-to previous}]]
        [:dispatch [:toast/error {:text "Could not assign task."}]]]})

(defevent ::assign-resolved
  [db task-uuid response]
  (patch-task db task-uuid
              #(assoc % :task/assigned-to
                      (:task/assigned-to (get response 'task/assign)))))

(defevent-fx ::update-failed [{:keys [db]} task-uuid rollback response]
  {:db (patch-task db task-uuid
                   (fn [task]
                     (reduce-kv (fn [t k v]
                                  (if (nil? v) (dissoc t k) (assoc t k v)))
                                task rollback)))
   :fx [[:dispatch [:toast/error {:text (error-text response)}]]]})

;; ---------------------------------------------------------------------------
;; Dropped linked entities (contacts / notes / tasks). `::save-edit` dispatches
;; these for the chips the composer dropped; its staged patch already hides
;; them, so each just detaches through its `task/remove-*` mutation and
;; re-resolves the detail page's entity-store copy to server truth.
;; ---------------------------------------------------------------------------

(defn- links-refresh-fx
  "Re-resolve the detail page's entity-store copy of the task after a link
   mutation, so its `:task/contacts` / `:task/notes` / `:task/tasks` catch up.
   Guarded on the task being in the store — links only surface on the detail
   page, whose route controller seeded it there."
  [db task-uuid]
  (cond-> []
    (get-in db (task-ident task-uuid))
    (conj [:dispatch [:entity/resolve {:task/uuid task-uuid}]])))

(defevent-fx ::remove-contact [{:keys [db]} {task-uuid :task/uuid contact-uuid :contact/uuid}]
  {:resolve {:query [{(list 'task/remove-contact {:task/uuid task-uuid
                                                  :contact/uuid contact-uuid})
                      [:task/uuid {:task/contacts [:contact/uuid :contact/full-name :contact/organisation?]}]}]
             :on-success-fx (links-refresh-fx db task-uuid)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove contact. Try again."}]]]}})

(defevent-fx ::remove-note-link [{:keys [db]} {task-uuid :task/uuid note-uuid :note/uuid}]
  {:resolve {:query [{(list 'task/remove-note {:task/uuid task-uuid
                                               :note/uuid note-uuid})
                      [:task/uuid {:task/notes [:note/uuid :note/text]}]}]
             :on-success-fx (links-refresh-fx db task-uuid)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove linked note. Try again."}]]]}})

;; The linked task's uuid rides as `:task/linked-uuid` (both entities are tasks;
;; the backend's `task/remove-task` disambiguates the same way).
(defevent-fx ::remove-task-link [{:keys [db]} {task-uuid :task/uuid linked-uuid :task/linked-uuid}]
  {:resolve {:query [{(list 'task/remove-task {:task/uuid task-uuid
                                               :task/linked-uuid linked-uuid})
                      [:task/uuid {:task/tasks [:task/uuid :task/title]}]}]
             :on-success-fx (links-refresh-fx db task-uuid)
             :on-error-fx [[:dispatch [:toast/error {:text "Could not remove linked task. Try again."}]]]}})

(defevent ::clear [db _]
  (dissoc db ::tasks ::load-result ::highlighted-uuid))

(defsub ::list [db]
  (get db ::tasks []))

(defsub ::loaded? [db]
  (contains? db ::tasks))

(defsub ::highlighted? [db task-uuid]
  (= task-uuid (::highlighted-uuid db)))

;; Today's completions carried in the loaded slice (Home only — the page loads
;; open tasks only), oldest-first for the progress timeline.
(defsub ::completed-today
  [db]
  (->> (get db ::tasks [])
       ;; Only actual completions — an open task's `(js/Date. nil)` → NaN would
       ;; poison the Progress timeline's window math.
       (filter :task/completed-at)
       (sort-by #(.getTime (js/Date. (:task/completed-at %))))))

;; Open tasks in the loaded slice whose deadline lands on today, earliest first.
;; Home's Progress card shows these as pills beside the day's all-day calendar
;; events. Scope follows the slice: `:user/current-tasks` is assigned-to-me, so a
;; task shared with the user but assigned elsewhere doesn't appear — the same
;; scope every other card on the page uses.
(defsub ::deadlines-today
  [tasks <- [::list]]
  (->> tasks
       (filter deadline/due-today?)
       (sort-by (juxt #(.getTime (:task/deadline-at %)) :task/title))))

(defn- sort-bucket
  "Newest first by creation time."
  [tasks]
  (sort-by #(.getTime (js/Date. (:task/created-at %))) > tasks))

(defn bucket-tasks
  "Group tasks into :now / :next / :later by urgency, each newest-first.
   Untriaged tasks (no urgency) land in :later (see `task-bucket`)."
  [tasks]
  (let [grouped (group-by task-bucket tasks)]
    {:now   (sort-bucket (:now grouped))
     :next  (sort-bucket (:next grouped))
     :later (sort-bucket (:later grouped))}))

(defsub ::buckets
  [tasks <- [::list]]
  (bucket-tasks tasks))

(def bucket-tabs
  "Ordered tabs over `::buckets`: bucket id and display label."
  [{:id :now :label "Now"}
   {:id :next :label "Next"}
   {:id :later :label "Later"}])
